import { Hidden } from '@mui/material'
import mixpanel from 'mixpanel-browser'
import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
// import { ContentPagination } from 'src/components/ContentPagination/ContentPagination'
import { Footer } from 'src/components/Footer/Footer'
// import { Breadcrumb } from 'src/components/global/Breadcrumb'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
// import { PremiumModal } from 'src/components/global/PremiumModal'
// import { SearchField } from 'src/components/global/SearchField'
// import { Sidebar } from 'src/components/global/Sidebar'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { IltaHeader } from 'src/components/IltaHeader/IltaHeader'
// import { PhasesSection } from 'src/components/PhasesSection/PhasesSection'
import { ReCaptcha } from 'src/components/ReCaptcha/ReCaptcha'
import { SolutionView } from 'src/components/SolutionView/SolutionView'
import { TopicCategoriesTab } from 'src/components/Tabs/TopicCategoriesTab'
import { TopicHeader } from 'src/components/TopicHeader/TopicHeader'
// import { VendorPagination } from 'src/components/VendorPagination/VendorPagination'
import { MIXPANEL_ENABLED, THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import { ITopicFullItemFragment } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption } from 'src/types'
import { getSolutionTypeFromTopic } from 'src/utils/getSolutionTypeFromTopic'
import { getTopicTabUrl } from 'src/utils/getTopicTabUrl'
import { withRetries } from 'src/utils/retryHelper'
import { getContentFilters, getFilters } from 'src/utils/TopicHelpers'

type Props = {
  topic: ITopicFullItemFragment
  filters: FilterOption[]
  contentFilters: FilterOption[]
}

const PremiumModal = dynamic(() => import('src/components/global/PremiumModal').then((mod) => mod.PremiumModal), {
  loading: () => <p className="loading-placeholder"></p>,
})
const Breadcrumb = dynamic(() => import('src/components/global/Breadcrumb').then((mod) => mod.Breadcrumb), {
  loading: () => <p className="loading-placeholder"></p>,
})
const SearchField = dynamic(() => import('src/components/global/SearchField').then((mod) => mod.SearchField), {
  loading: () => <p className="loading-placeholder"></p>,
})
const Sidebar = dynamic(() => import('src/components/global/Sidebar').then((mod) => mod.Sidebar), {
  loading: () => <p className="loading-placeholder"></p>,
})
const PhasesSection = dynamic(
  () => import('src/components/PhasesSection/PhasesSection').then((mod) => mod.PhasesSection),
  {
    loading: () => <p className="loading-placeholder"></p>,
  },
)
const VendorPagination = dynamic(
  () => import('src/components/VendorPagination/VendorPagination').then((mod) => mod.VendorPagination),
  {
    loading: () => <p className="loading-placeholder"></p>,
  },
)
const ContentPagination = dynamic(
  () => import('src/components/ContentPagination/ContentPagination').then((mod) => mod.ContentPagination),
  {
    loading: () => <p className="loading-placeholder"></p>,
  },
)

const getTabIndexFromRouter = (hasContents: boolean, isPremiumCategory: boolean, tab?: string | string[]) => {
  if (isPremiumCategory && tab === 'overview') return 0
  if (tab === 'directory') return 1
  if (hasContents && tab === 'content') return 2
  return isPremiumCategory ? 0 : 1
}

const TopicPage: NextPage<Props> = ({ topic, filters, contentFilters }: Props) => {
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()
  const [showModal, setShowModal] = useState(false)
  const { isPremium, status } = useAuthenticated()
  // const filtersDisabled = useMemo(() => Boolean(topic.attributes?.isPremium && !isPremium), [isPremium, topic])
  const [vendorTotalCount, setVendorTotalCount] = useState(0)
  const hasContents = !!topic?.contents && topic?.contents.length > 0
  const [tabIndex, setTabIndex] = useState(0)
  const name = topic?.name || ''
  const description = topic?.description || ''
  const publishedAt = topic?.publishedAt || ''

  const topicId = topic?.documentId || ''
  const slug = topic?.slug || ''
  const subTopics = topic?.subTopics || []
  const isPremiumCategory = !!topic?.isPremium
  const showOverviewTab = isPremium && isPremiumCategory
  const [filterHeight, setFilterHeight] = useState(false)

  // Get search keyword from query parameters for highlighting (reactive to router changes)
  const searchKeyword = useMemo(() => {
    if (!router.isReady) return undefined
    const keyword = router.query.keyword
    if (!keyword) return undefined

    let keywordStr: string
    if (Array.isArray(keyword)) {
      keywordStr = keyword[0] || ''
    } else {
      keywordStr = keyword as string
    }

    // Decode URI component in case it's URL encoded (handles + as space, %20, etc.)
    try {
      return decodeURIComponent(keywordStr)
    } catch {
      // If decoding fails, return as is
      return keywordStr || undefined
    }
  }, [router.isReady, router.query.keyword])

  const meta = (
    <Meta
      title={name}
      description={description}
      url={`/topics/${slug}/`}
      keywords={(subTopics || [])
        .map((subTopic: any) => subTopic?.name || '')
        .concat((subTopics || []).map((subTopic: any) => subTopic?.description || ''))}
      article={{
        authors: ['Nicola (Nikki) Shaver, Jeroen Plink'],
        section: 'Product Type',
        publishedTime: publishedAt,
        modifiedTime: publishedAt,
      }}
    />
  )

  useEffect(() => {
    const newTabIndex = getTabIndexFromRouter(hasContents, isPremiumCategory, router.query.tab)
    if (isPremium && tabIndex !== newTabIndex) {
      setTabIndex(newTabIndex)
    }
    if (!isPremium && newTabIndex === 0) {
      if (status === 'loading') {
        setTabIndex(-1)
      } else {
        setTabIndex(1)
      }
    } else {
      setTabIndex(newTabIndex)
    }
  }, [router, isPremium, status])

  useEffect(() => {
    if (MIXPANEL_ENABLED) {
      mixpanel.track('visit_topic', {
        topicId: topic?.documentId,
        topicName: name,
        topicSlug: slug,
      })
    }
  }, [topic])

  const solutionType = getSolutionTypeFromTopic(topic)

  const onSelectTab = (tabId: number) => {
    setTabIndex(tabId)
    let newTab = 'directory'
    let defaultSort = 'Default'
    if (tabId === 0) {
      newTab = 'overview'
    } else if (tabId === 1) {
      newTab = 'directory'
      defaultSort = 'Default'
    } else if (tabId === 2) {
      newTab = 'content'
      defaultSort = 'Category_Chronologically'
    }
    const keyword = router.query.keyword
    defaultSort = searchKeyword ? 'bykeyword' : defaultSort

    updateQueryParams({
      tab: newTab,
      sort: defaultSort,
      page: undefined,
      keyword: (keyword as string | undefined) || undefined,
    })
  }

  return (
    <ReCaptcha>
      <Main meta={meta}>
        <ThemeOnly theme="legaltech">
          <PremiumModal origin={`/topics/${slug}/`} isOpen={showModal} onClose={() => setShowModal(false)} />
        </ThemeOnly>
        <MainContainer id="mainContainer">
          <ThemeOnly theme="ilta">
            <IltaHeader />
          </ThemeOnly>
          <div className="flex flex-col bg-clouds">
            {topic && <Breadcrumb subject={name} />}
            {topic && <TopicHeader topic={topic} subtopicButtonFlag={false} />}
            <ThemeOnly theme="legaltech">
              <div role="tablist" className="h-[50px] flex flex-row justify-center">
                {showOverviewTab && (
                  <TopicCategoriesTab id={0} activeTab={tabIndex} onSelectTab={onSelectTab}>
                    OVERVIEW
                  </TopicCategoriesTab>
                )}
                <TopicCategoriesTab id={1} activeTab={tabIndex} onSelectTab={onSelectTab}>
                  DIRECTORY {vendorTotalCount > 0 && `(${vendorTotalCount})`}
                </TopicCategoriesTab>
                <ThemeOnly theme="legaltech">
                  {hasContents && (
                    <TopicCategoriesTab id={2} activeTab={tabIndex} onSelectTab={onSelectTab}>
                      CONTENT
                    </TopicCategoriesTab>
                  )}
                </ThemeOnly>
              </div>
            </ThemeOnly>
          </div>
          <hr />
          <div className="flex flex-col">
            <div className="flex justify-center">
              {showOverviewTab && tabIndex === 0 && (
                <div className="!max-w-[1100px] flex flex-col justify-between w-full mb-32">
                  <SolutionView
                    topicId={topicId}
                    type={solutionType}
                    url={getTopicTabUrl(`/topics/${slug}`, 'directory', router.query)}
                  />
                  <PhasesSection
                    topicSlug={slug}
                    topicName={name}
                    viewAllUrl={getTopicTabUrl(`/topics/${slug}`, 'content', router.query)}
                  />
                </div>
              )}
              {tabIndex === 1 && (
                <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32 relative">
                  <Hidden mdDown>
                    <div className="w-1/4 pr-10 flex flex-col">
                      <SearchField className="my-8" />
                      <Sidebar filterOptions={filters} defaultSelectFilterIdx={1} filterHeight={filterHeight} />
                    </div>
                  </Hidden>
                  {topic && (
                    <VendorPagination
                      filterProps={filters}
                      topic={topic}
                      solutionType={solutionType}
                      changeTotalCount={setVendorTotalCount}
                      includeCompany={false}
                      setFilterHeight={setFilterHeight}
                    />
                  )}
                </div>
              )}
              {hasContents && tabIndex === 2 && (
                <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
                  <Hidden mdDown>
                    <div className="w-1/4 pr-10 flex flex-col">
                      <SearchField className="my-8" />
                      <Sidebar
                        filterOptions={contentFilters}
                        // filtersDisabled={filtersDisabled}
                        defaultSelectFilterIdx={1}
                        onClickWhenDisabled={() => setShowModal(true)}
                      />
                    </div>
                  </Hidden>
                  {topic && (
                    <ContentPagination
                      filterProps={contentFilters}
                      preAppliedFilters={{ topicIds: topic.documentId! }}
                      // filtersDisabled={filtersDisabled}
                      onClickWhenDisabled={() => setShowModal(true)}
                      sortType="category_contents"
                      searchKeyword={searchKeyword}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <ThemeOnly theme="legaltech">
            <Footer />
          </ThemeOnly>
        </MainContainer>
      </Main>
    </ReCaptcha>
  )
}

export default TopicPage

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  if (!params?.slug) {
    return {
      revalidate: 60,
      notFound: true,
    }
  }
  const slug = params.slug as string
  try {
    // const { slugUpdates } = await apolloSdk.getSlugUpdates({ type: 'topics', oldSlug: slug })
    // if (slugUpdates && slugUpdates?.data.length > 0) {
    //   slug = slugUpdates.data[0].attributes?.newSlug || slug
    // }

    const { topics } = await withRetries(() => apolloSdk.getTopicBySlug({ slug }))
    const topic = topics && topics[0]

    if (!topic) {
      return {
        revalidate: 60,
        notFound: true,
      }
    }

    const filters: FilterOption[] = await getFilters('topic', topic, slug, THEME)
    const contentFilters: FilterOption[] = await getContentFilters()

    // console.log('contentFilters ------------- \n', contentFilters)
    return {
      props: {
        topic,
        filters,
        contentFilters,
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching topic:', error)
    return {
      notFound: true,
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  const { topics } = await apolloSdk.getTopics()
  const paths: Array<{ params: { slug: string } }> = []
  if (topics) {
    topics.forEach((topic: any) => {
      if (!topic) return
      paths.push({ params: { slug: topic?.slug || '' } })
    })
  }

  return {
    paths,
    fallback: 'blocking',
  }
}
